import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import { ButtonProps, Menu, MenuList } from '@mui/material';
import Button from '@mui/material/Button';
import { Permission } from 'interfaces/permissionOption';
import { ReviewForm } from 'interfaces/reviewForm';
import { Study } from 'interfaces/study';
import React from 'react';
import { useStudyExportController } from 'services/export/useStudyExportController';
import { StringParam, useQueryParam } from 'use-query-params';
import { usePermissions } from 'utils/usePermissions';
import IntermediateResultsDownloadModal from './IntermediateResultsDownloadModal';
import SelectPrepareJobToExportModal from './SelectPrepareJobToExportModal';
import StudyExportMenuListContents from './StudyExportMenuListContents';

interface LegacyStudyExportMenuProps {
  children?: React.ReactNode;
  study?: Study;
  skipPreloadingStudyStains?: boolean; // If true, skip querying stain types in the study (i.e. when used in list of studies)
  disabled: boolean;
  forms?: ReviewForm[];
  triggerButtonColor?: ButtonProps['color'];
  triggerButtonVariant?: ButtonProps['variant'];
}

const LegacyStudyExportMenu: React.FunctionComponent<LegacyStudyExportMenuProps> = ({
  study,
  skipPreloadingStudyStains,
  disabled,
  forms,
  triggerButtonColor = 'secondary',
  triggerButtonVariant = 'contained',
}) => {
  const activeStudyId = study?.id;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const { hasPermission } = usePermissions();

  const canDownloadIntermediateResults = hasPermission(Permission.DownloadIntermediateResults);
  const { startExport, exportProgressDialog, isExportRunning } = useStudyExportController();

  const startImmediateExport: typeof startExport = (operation) => {
    handleClose();
    return startExport(operation);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuOpen = Boolean(anchorEl);

  const [selectPrepareJobToExportModalStudyId, setSelectPrepareJobToExportModalStudyId] = useQueryParam(
    'selectPrepareJobToExportModalStudyId',
    StringParam
  );
  const isSelectPrepareJobToExportModalOpen =
    Boolean(activeStudyId) && selectPrepareJobToExportModalStudyId === activeStudyId;
  const [intermediateResultsDownloadModalStudyId, setIntermediateResultsDownloadModalStudyId] = useQueryParam(
    'intermediateResultsDownloadModalStudyId',
    StringParam
  );
  const isIntermediateResultsDownloadModalOpen =
    Boolean(activeStudyId) && intermediateResultsDownloadModalStudyId === activeStudyId;

  return (
    <>
      <Button
        onClick={handleClick}
        color={triggerButtonColor}
        disabled={disabled}
        endIcon={menuOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        variant={triggerButtonVariant}
        size="small"
      >
        Export
      </Button>
      <Menu
        onClick={(event) => event.stopPropagation()}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleClose}
      >
        <MenuList>
          <StudyExportMenuListContents
            study={study}
            forms={forms}
            skipPreloadingStudyStains={skipPreloadingStudyStains}
            disabled={disabled}
            immediateExportDisabled={isExportRunning}
            startImmediateExport={startImmediateExport}
          />
        </MenuList>
      </Menu>
      {exportProgressDialog}

      {Boolean(study?.allowExport && study?.hasResults && isSelectPrepareJobToExportModalOpen) && (
        <SelectPrepareJobToExportModal
          open={isSelectPrepareJobToExportModalOpen}
          onClose={() => setSelectPrepareJobToExportModalStudyId(undefined)}
          studyId={activeStudyId}
        />
      )}
      {canDownloadIntermediateResults && isIntermediateResultsDownloadModalOpen && (
        <IntermediateResultsDownloadModal
          open={isIntermediateResultsDownloadModalOpen}
          onClose={() => setIntermediateResultsDownloadModalStudyId(undefined)}
        />
      )}
    </>
  );
};

export default LegacyStudyExportMenu;
